// InPersonSignature.jsx
// Route: /sign/:token  (unguessable public token — never the numeric order id)
// Opens in a new window on the second monitor.
// Client signs with finger/stylus → hits /api/rentals/orders/:id/sign
// On success → postMessage to opener, window closes.

import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from './lib/axiosClient';
import { fmtDate as fmtDateShared } from './lib/fmtDate';

const fmtDate = (d) => fmtDateShared(d, { month: 'long', day: 'numeric', year: 'numeric' });
function fmtMoney(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function InPersonSignature() {
  const { token } = useParams(); // unguessable public token — never the numeric order id
  const canvasRef   = useRef(null);
  const drawing     = useRef(false);
  const lastPos     = useRef(null);

  const [order,     setOrder]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [signed,    setSigned]    = useState(false);
  const [agreed,    setAgreed]    = useState(false);
  const [submitting,setSubmitting]= useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState('');
  const [signerName,setSignerName]= useState('');
  const [termsDocs, setTermsDocs] = useState([]);
  const [pdfAcks,   setPdfAcks]   = useState({}); // { [docId]: bool } — one required ack per attached PDF
  const pdfDocs = termsDocs.filter(td => td.format === 'pdf_upload');
  const allPdfsAcked = pdfDocs.every(td => pdfAcks[td.id]);

  function loadTerms() {
    return axios.get(`/api/public/rentals/o/${token}/terms-preview`)
      .then(r => setTermsDocs(r.data.terms || []))
      .catch(() => {});
  }

  // Load order + the real current rental terms (not hardcoded)
  useEffect(() => {
    axios.get(`/api/public/rentals/o/${token}`)
      .then(r => {
        setOrder(r.data.order || r.data);
        setSignerName(r.data.order?.client_name || r.data?.client_name || '');
      })
      .catch(() => setError('Could not load order. Please close and try again.'))
      .finally(() => setLoading(false));
    loadTerms();
  }, [token]);

  // Init canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [order]);

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    };
  }

  function startDraw(e) {
    e.preventDefault();
    drawing.current = true;
    const pos = getPos(e, canvasRef.current);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.5, 0, Math.PI * 2);
    ctx.fillStyle = '#0f172a';
    ctx.fill();
  }

  function draw(e) {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setSigned(true);
  }

  function stopDraw(e) { e.preventDefault(); drawing.current = false; }

  function clearSig() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
  }

  async function submit() {
    if (!signed)  { setError('Please draw your signature.'); return; }
    if (!agreed)  { setError('Please check the agreement box.'); return; }
    if (!allPdfsAcked) { setError('Please confirm you have read each attached document.'); return; }
    if (!signerName.trim()) { setError('Please enter your name.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const sigData = canvasRef.current.toDataURL('image/png');
      const res = await axios.post(`/api/public/rentals/o/${token}/sign`, {
        signer_name:  signerName,
        signer_email: order?.client_email || '',
        signer_phone: order?.client_phone || '',
        signature_data: sigData,
        contract_terms_ids: termsDocs.map(td => td.id),
      });
      if (res.data.success) {
        // Notify opener to refresh
        if (window.opener) {
          window.opener.postMessage({ type: 'ORDER_SIGNED', orderId: order?.id }, '*');
        }
        // Redirect to Stripe checkout
        try {
          const stripeRes = await axios.post('https://ivision-crm-backend-production.up.railway.app/api/public/rentals/stripe/checkout', {
            order_token: token,
            payment_type: 'deposit',
            customer_email: order?.client_email || '',
            customer_name: signerName,
          });
          if (stripeRes.data?.url) {
            window.location.href = stripeRes.data.url;
          } else {
            setDone(true);
            setTimeout(() => window.close(), 3000);
          }
        } catch(stripeErr) {
          console.error('Stripe checkout error:', stripeErr);
          setDone(true);
          setTimeout(() => window.close(), 3000);
        }
      } else if (res.data.error === 'terms_changed') {
        setError(res.data.message || 'The rental terms were updated. Please review them again.');
        await loadTerms();
        setPdfAcks({});
      } else {
        setError(res.data.error || 'Signature failed. Please try again.');
      }
    } catch(e) {
      if (e.response?.data?.error === 'terms_changed') {
        setError(e.response.data.message || 'The rental terms were updated. Please review them again.');
        await loadTerms();
        setPdfAcks({});
      } else {
        setError(e.response?.data?.error || 'Network error. Please try again.');
      }
    }
    setSubmitting(false);
  }

  // ── STYLES ─────────────────────────────────────────────────
  const S = {
    page: {
      minHeight: '100vh', background: '#f8fafc',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'flex-start', padding: '24px 16px',
      fontFamily: "'Inter', -apple-system, sans-serif",
    },
    card: {
      width: '100%', maxWidth: 640, background: '#fff',
      borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
      padding: '28px 32px', marginBottom: 16,
    },
    header: {
      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
      paddingBottom: 16, borderBottom: '1px solid #e2e8f0',
    },
    orderBox: {
      background: '#f8fafc', borderRadius: 10,
      padding: '14px 16px', marginBottom: 20,
      border: '1px solid #e2e8f0', fontSize: 13, color: '#374151',
    },
    label: {
      fontSize: 11, fontWeight: 600, color: '#6b7280',
      textTransform: 'uppercase', letterSpacing: '.4px',
      display: 'block', marginBottom: 5,
    },
    input: {
      width: '100%', padding: '10px 14px', borderRadius: 8,
      border: '1px solid #e2e8f0', fontSize: 15, color: '#0f172a',
      outline: 'none', boxSizing: 'border-box', marginBottom: 16,
    },
    terms: {
      background: '#fff', border: '1px solid #e2e8f0',
      borderRadius: 8, padding: 14, maxHeight: 160,
      overflowY: 'auto', fontSize: 11, color: '#374151',
      lineHeight: 1.65, marginBottom: 16,
    },
    checkRow: (checked) => ({
      display: 'flex', gap: 10, alignItems: 'flex-start',
      cursor: 'pointer', padding: '10px 14px',
      background: checked ? '#f0fdf4' : '#f8fafc',
      borderRadius: 8,
      border: `1px solid ${checked ? '#bbf7d0' : '#e2e8f0'}`,
      marginBottom: 16,
    }),
    btnPrimary: (disabled) => ({
      width: '100%', padding: '16px 0', borderRadius: 10,
      border: 'none', fontSize: 16, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      background: disabled ? '#d1d5db' : '#16a34a', color: '#fff',
      transition: 'background 0.2s',
    }),
    btnClear: {
      padding: '6px 16px', borderRadius: 6,
      border: '1px solid #e2e8f0', background: '#fff',
      color: '#6b7280', fontSize: 12, cursor: 'pointer',
    },
  };

  // ── DONE STATE ─────────────────────────────────────────────
  if (done) {
    return (
      <div style={S.page}>
        <div style={{ ...S.card, textAlign: 'center', paddingTop: 48, paddingBottom: 48 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
            Agreement Signed!
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>
            Signature saved to order #{order?.order_number || order?.id}.
          </p>
          <p style={{ fontSize: 13, color: '#94a3b8' }}>
            This window will close automatically…
          </p>
        </div>
      </div>
    );
  }

  // ── LOADING / ERROR ────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ ...S.page, justifyContent: 'center' }}>
        <div style={{ fontSize: 16, color: '#6b7280' }}>Loading order…</div>
      </div>
    );
  }

  if (!order && error) {
    return (
      <div style={{ ...S.page, justifyContent: 'center' }}>
        <div style={{ color: '#dc2626', fontWeight: 600 }}>{error}</div>
      </div>
    );
  }

  const items   = order?.rental_order_items || [];
  const total   = order?.total || order?.subtotal || 0;

  // ── MAIN ──────────────────────────────────────────────────
  return (
    <div style={S.page}>

      {/* Header */}
      <div style={{ ...S.card, padding: '18px 32px' }}>
        <div style={S.header}>
          <span style={{ fontSize: 28 }}>🚧</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>{order.business_name || 'Equipment Rentals'}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>Equipment Rental Agreement — In-Person Signature</div>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Order</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#f97316' }}>#{order?.order_number || order?.id}</div>
          </div>
        </div>

        {/* Order summary */}
        <div style={S.orderBox}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: items.length ? 12 : 0 }}>
            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>CLIENT</div>
              <div style={{ fontWeight: 700 }}>{order.client_name || '—'}</div>
              {order.client_company && <div style={{ fontSize: 12, color: '#6b7280' }}>{order.client_company}</div>}
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>RENTAL PERIOD</div>
              <div style={{ fontWeight: 600 }}>{fmtDate(order.from_date)} → {fmtDate(order.to_date)}</div>
            </div>
          </div>
          {items.length > 0 && (
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 4 }}>
              {items.map((li, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
                  <span>{li.item_name} × {li.quantity}</span>
                  <span style={{ fontWeight: 600 }}>{fmtMoney(li.total_price || li.subtotal)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, borderTop: '2px solid #0f172a', marginTop: 8, paddingTop: 8 }}>
                <span>Total</span>
                <span style={{ color: '#f97316' }}>{fmtMoney(total)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Signature card */}
      <div style={S.card}>

        {/* Signer name */}
        <label style={S.label}>Your Full Name <span style={{ color: '#ef4444' }}>*</span></label>
        <input
          style={S.input}
          placeholder="Type your full legal name"
          value={signerName}
          onChange={e => setSignerName(e.target.value)}
        />

        {/* Terms — the tenant's real, current, owner-edited terms */}
        <div style={S.terms}>
          {termsDocs.length === 0 && <span style={{ color: '#94a3b8' }}>Loading terms…</span>}
          {termsDocs.map((td, i) => (
            <div key={i} style={{ marginBottom: i < termsDocs.length - 1 ? 16 : 0 }}>
              <strong style={{ fontSize: 12, color: '#0f172a' }}>{td.title}</strong><br /><br />
              {td.format === 'pdf_upload' ? (
                <>
                  The complete {td.title} (Version {td.version}) is provided as a separate document:{' '}
                  <a href={td.file_url} target="_blank" rel="noreferrer">View {td.title}</a>
                </>
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{td.renderedBody}</span>
              )}
            </div>
          ))}
        </div>

        {/* One required ack per attached PDF document — the signature pad
            below is disabled until every one of these is checked */}
        {pdfDocs.map(td => (
          <label key={td.id} style={S.checkRow(!!pdfAcks[td.id])}>
            <input
              type="checkbox"
              checked={!!pdfAcks[td.id]}
              onChange={e => setPdfAcks(p => ({ ...p, [td.id]: e.target.checked }))}
              style={{ marginTop: 2, width: 20, height: 20, cursor: 'pointer', flexShrink: 0 }}
            />
            <span>I have read and agree to <strong>{td.title}</strong> (Version {td.version}).</span>
          </label>
        ))}

        {/* Signature pad — disabled until every attached PDF is acked */}
        <label style={{ ...S.label, marginBottom: 8 }}>Signature</label>
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <canvas
            ref={canvasRef}
            width={1200}
            height={280}
            onMouseDown={allPdfsAcked ? startDraw : undefined}
            onMouseMove={allPdfsAcked ? draw : undefined}
            onMouseUp={allPdfsAcked ? stopDraw : undefined}
            onMouseLeave={allPdfsAcked ? stopDraw : undefined}
            onTouchStart={allPdfsAcked ? startDraw : undefined}
            onTouchMove={allPdfsAcked ? draw : undefined}
            onTouchEnd={allPdfsAcked ? stopDraw : undefined}
            style={{
              width: '100%', height: 200,
              border: `2px dashed ${signed ? '#0f172a' : '#cbd5e1'}`,
              borderRadius: 10, cursor: allPdfsAcked ? 'crosshair' : 'not-allowed',
              background: allPdfsAcked ? '#fff' : '#f1f5f9', display: 'block',
              touchAction: 'none', opacity: allPdfsAcked ? 1 : 0.6,
            }}
          />
          {!signed && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)',
              pointerEvents: 'none', textAlign: 'center',
            }}>
              <div style={{ fontSize: 48, color: '#e2e8f0' }}>✍️</div>
              <div style={{ fontSize: 14, color: '#94a3b8', marginTop: 6 }}>{allPdfsAcked ? 'Sign here with your finger or stylus' : 'Confirm the document(s) above first'}</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            Signing as: <strong style={{ color: '#374151' }}>{signerName || '—'}</strong>
          </span>
          <button onClick={clearSig} style={S.btnClear}>Clear</button>
        </div>

        {/* Agree checkbox */}
        <label style={S.checkRow(agreed)}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            style={{ marginTop: 2, width: 20, height: 20, cursor: 'pointer', flexShrink: 0 }}
          />
          <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
            I, <strong>{signerName || '___'}</strong>, have read and agree to all terms and conditions of this Equipment Rental Agreement.
            I understand this constitutes a legally binding electronic signature.
          </span>
        </label>

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', borderRadius: 8,
            fontSize: 13, color: '#dc2626', marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={submit}
          disabled={submitting || !signed || !agreed || !allPdfsAcked || !signerName.trim()}
          style={S.btnPrimary(submitting || !signed || !agreed || !allPdfsAcked || !signerName.trim())}
        >
          {submitting ? '⏳ Saving Signature…' : '✅ Submit Signature & Complete'}
        </button>
      </div>
    </div>
  );
}
